import React from "react"
import * as React_ from "elm-ts/lib/React"
import * as M from "../Model/viewingsModel"
import { flow, pipe } from "fp-ts/lib/function"
import * as O from "fp-ts/Option"
import * as E from "fp-ts/Either"
import dayjs from "dayjs"
import { ChevronRight, Close } from "../../components/Icon"
import {
  Calendar,
  nextMonthCalendar,
  prevMonthCalendar,
} from "../../lib/calendar"
import { AgentViewing } from "@flypengine/myFlyp-lib"
import * as A from "fp-ts/lib/Array"
import { BrandSpinner } from "../../components"
import { Error } from "../components"
import { Types } from "@flypengine/lib-shared"
import Modal from "../../components/Modal"

export function viewingsView<msg>(
  config: M.Config<msg>
): (model: M.Model) => React_.Html<msg> {
  return model => dispatch => {
    return pipe(
      model.calendarModel,
      E.match(
        status => {
          switch (status) {
            case "Loading": {
              return (
                <div data-cy="dashboard-viewings-loader">
                  <BrandSpinner description="Loading viewings" />
                </div>
              )
            }

            case "Error": {
              return (
                <Error
                  dataCy="dashboard-viewings-error"
                  message="Data not found. If this doesn't look right please"
                />
              )
            }
            case "Not Authorized": {
              return (
                <Error
                  dataCy="dashboard-viewings-error-auth"
                  message="You are not authorized to see this content. If this doesn't look right please"
                />
              )
            }

            default: {
              return <></>
            }
          }
        },
        ({ entries, selectedDay, upcoming }) => (
          <>
            <div className="grid px-4 lg:px-10 gap-6 lg:gap-4 py-4 max-w-[1500px] mx-auto lg:grid-cols-[300px_4fr] lg:h-[calc(100vh-80px)] lg:max-h-[800px]">
              <div className="lg:col-start-2 sm:bg-stone sm:p-4 rounded-xl flex flex-col">
                {monthCalendarView({
                  contents: {
                    calendar: model.calendar,
                    entries,
                    calendarAvailability:
                      model.calendarSetup.newCalendarAvailability,
                    calendarOverride: model.calendarSetup.calendarOverride,
                  },
                  updateCalendarContents: contents =>
                    pipe(contents, M.Msg.calendarContents),
                  updateCalendarDayModel: contents =>
                    pipe(contents, M.Msg.calendarDayModel),
                })(flow(config.wrapperMsg, dispatch))}
              </div>
              <div className="lg:relative lg:col-start-1 lg:col-end-2 lg:row-start-1 flex flex-col lg:justify-between gap-6 lg:gap-4">
                {selectionView({
                  contents: {
                    upcoming,
                    selectedDay,
                  },
                  updateCalendarDayModel: contents =>
                    pipe(contents, M.Msg.calendarDayModel),
                })(flow(config.wrapperMsg, dispatch))}
                {moreInfoView(model)(flow(config.wrapperMsg, dispatch))}
              </div>
            </div>
          </>
        )
      )
    )
  }
}

const monthCalendarView = ({
  contents,
  updateCalendarContents,
  updateCalendarDayModel,
}: {
  contents: {
    calendar: O.Option<Calendar>
    entries: AgentViewing[]
    calendarAvailability: O.Option<Types.CalendarAvailability>
    calendarOverride: Types.CalendarOverride
  }
  updateCalendarContents: (contents: Calendar) => M.Msg
  updateCalendarDayModel: (contents: O.Option<string>) => M.Msg
}): React_.Html<M.Msg> => {
  return dispatch =>
    pipe(
      contents.calendar,
      O.match(
        () => <></>,
        calendar => (
          <>
            <div className="w-full max-w-[300px] mx-auto flex items-center justify-between mb-4">
              <button
                className="button-icon w-6 h-6 scale-x-[-100%]"
                data-cy="dashboard-viewings-prev-month"
                onClick={() =>
                  pipe(
                    prevMonthCalendar(new Date(calendar.current)),
                    updateCalendarContents,
                    dispatch
                  )
                }
              >
                <ChevronRight />
              </button>
              <h3
                className="heading text-center"
                data-cy="dashboard-viewings-title"
              >
                {calendar.title}
              </h3>
              <button
                className="button-icon w-6 h-6"
                data-cy="dashboard-viewings-next-month"
                onClick={() =>
                  pipe(
                    nextMonthCalendar(calendar.current),
                    updateCalendarContents,
                    dispatch
                  )
                }
              >
                <ChevronRight />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 sm:gap-2 lg:gap-3 mb-2">
              {calendar.weekdays.map(day => (
                <p className="text-lg" key={day}>
                  {day}
                </p>
              ))}
            </div>
            <div className="grow grid grid-cols-7 gap-1 sm:gap-2 lg:gap-3 auto-rows-fr min-h-[320px] sm:min-h-[400px]">
              {calendar.entries.map((entry, idx) => {
                const matchedViewings = M.matchedViewings(
                  contents.entries,
                  entry.date
                )
                // Check if this day of the week has availability
                const dayOfWeek = dayjs(entry.date)
                  .day()
                  .toString() as keyof Types.CalendarAvailability
                const hasAvailability = pipe(
                  contents.calendarAvailability,
                  O.chain(avail => avail[dayOfWeek]),
                  O.exists(slots => A.isNonEmpty(slots))
                )

                // Check if date is fully blocked by calendar override
                const isBlockedByOverride = pipe(
                  contents.calendarOverride,
                  O.match(
                    () => false,
                    overrides =>
                      pipe(
                        overrides,
                        A.some(override => {
                          const entryDate = dayjs(entry.date).startOf("day")
                          const startDate = dayjs(override.dates[0]).startOf(
                            "day"
                          )
                          const endDate = dayjs(override.dates[1]).startOf(
                            "day"
                          )
                          const isInRange =
                            (entryDate.isAfter(startDate) ||
                              entryDate.isSame(startDate)) &&
                            (entryDate.isBefore(endDate) ||
                              entryDate.isSame(endDate))
                          // Check if override has no time slots, or all time slots are empty/invalid
                          const hasNoValidTimeSlots =
                            A.isEmpty(override.times) ||
                            pipe(
                              override.times,
                              A.every(
                                slot =>
                                  !slot.start ||
                                  !slot.end ||
                                  slot.start === "" ||
                                  slot.end === ""
                              )
                            )
                          return isInRange && hasNoValidTimeSlots
                        })
                      )
                  )
                )

                // Check if date has an override with valid time slots
                const hasOverrideWithAvailability = pipe(
                  contents.calendarOverride,
                  O.match(
                    () => false,
                    overrides =>
                      pipe(
                        overrides,
                        A.some(override => {
                          const entryDate = dayjs(entry.date).startOf("day")
                          const startDate = dayjs(override.dates[0]).startOf(
                            "day"
                          )
                          const endDate = dayjs(override.dates[1]).startOf(
                            "day"
                          )
                          const isInRange =
                            (entryDate.isAfter(startDate) ||
                              entryDate.isSame(startDate)) &&
                            (entryDate.isBefore(endDate) ||
                              entryDate.isSame(endDate))
                          // Check if override has valid time slots
                          const hasValidTimeSlots =
                            A.isNonEmpty(override.times) &&
                            pipe(
                              override.times,
                              A.some(
                                slot =>
                                  !!slot.start &&
                                  !!slot.end &&
                                  slot.start !== "" &&
                                  slot.end !== ""
                              )
                            )
                          return isInRange && hasValidTimeSlots
                        })
                      )
                  )
                )

                const shouldGreyOut =
                  (!hasAvailability && !hasOverrideWithAvailability) ||
                  entry.disabled ||
                  isBlockedByOverride
                return (
                  <div
                    key={idx}
                    className={`bg-white rounded-md p-1 sm:p-2 flex flex-col justify-between ${
                      shouldGreyOut ? "opacity-40" : ""
                    }`}
                    data-cy={`dashboard-viewings-calendar-day-${entry.date}`}
                    onClick={() =>
                      pipe(O.some(entry.date), updateCalendarDayModel, dispatch)
                    }
                  >
                    <time
                      className={`text-sm md:text-base ${
                        entry.isToday
                          ? "flex h-6 w-6 items-center justify-center rounded-full font-semibold text-white bg-brick"
                          : ""
                      }`}
                    >
                      {dayjs(entry.date).format("DD")}
                    </time>
                    <button
                      disabled={A.isEmpty(matchedViewings)}
                      className={`w-full h-full flex flex-col justify-end bg-white rounded-md ${
                        A.isEmpty(matchedViewings) ? "cursor-not-allowed" : ""
                      }`}
                    >
                      {A.isNonEmpty(matchedViewings) && (
                        <p
                          className=" text-[.625rem] sm:text-xs px-2 py-1 rounded-md bg-concrete"
                          data-cy={`dashboard-viewings-calendar-day-${entry.date}-viewing-counter`}
                        >
                          {matchedViewings.length}
                          <span className="hidden lg:inline ml-2">
                            {matchedViewings.length === 1
                              ? "Viewing"
                              : "Viewings"}
                          </span>
                        </p>
                      )}
                    </button>
                  </div>
                )
              })}
            </div>
          </>
        )
      )
    )
}

const selectionView = ({
  contents,
  updateCalendarDayModel,
}: {
  contents: {
    upcoming: O.Option<AgentViewing>
    selectedDay: O.Option<{ date: string; entries: AgentViewing[] }>
  }
  updateCalendarDayModel: (contents: O.Option<string>) => M.Msg
}): React_.Html<M.Msg> => {
  const Viewing = (viewing: AgentViewing, dispatch: (msg: M.Msg) => void) => (
    <div
      key={`${viewing.agent.id}-${viewing.viewingDate}`}
      className="card bg-concrete py-3 px-4 text-lg mb-2 last:mb-0 relative"
      data-cy={`dashboard-viewings-calendar-day-viewing-${dayjs(
        viewing.viewingDate
      ).format("YYYY-MM-DD")}`}
    >
      <div className="flex items-baseline">
        <div className="mr-4 w-[45px]">
          {viewing.viewingDate.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "Europe/London",
          })}
        </div>
        <div className="capitalize flex-grow">
          <p className="leading-5">{viewing.agent.brand}</p>
        </div>
      </div>
      {viewing.viewingDate > new Date() && (
        <button
          className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center text-brick hover:text-brick-dark bg-white rounded-full shadow-sm hover:shadow-md group"
          onClick={() => pipe(viewing, M.Msg.openCancellationModal, dispatch)}
          data-cy="viewing-request-cancellation"
        >
          <Close />
          <span className="absolute bottom-full right-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-[9999] pointer-events-none">
            Request cancellation
          </span>
        </button>
      )}
    </div>
  )
  return dispatch => (
    <div className="lg:grow sm:bg-stone sm:p-4 rounded-xl">
      <div>
        {pipe(
          contents.selectedDay,
          O.match(
            () => (
              <>
                <h3 className="heading mb-4">Your next viewing</h3>
                {pipe(
                  contents.upcoming,
                  O.match(
                    () => (
                      <div
                        className="card"
                        data-cy="dashboard-viewings-calendar-upcoming-empty"
                      >
                        No viewings yet, we’re on it!
                      </div>
                    ),
                    viewing => (
                      <div className="flex flex-col gap-4">
                        <div className="card">
                          <p
                            className="text-lg mb-4"
                            data-cy="dashboard-viewings-calendar-upcoming-title"
                          >
                            {dayjs(viewing.viewingDate).format("dddd Do")}
                          </p>
                          {Viewing(viewing, dispatch)}
                        </div>
                      </div>
                    )
                  )
                )}
              </>
            ),
            ({ date, entries }) => (
              <>
                <div className="flex justify-between">
                  <h3
                    className="heading mb-4"
                    data-cy="dashboard-viewings-calendar-day-title"
                  >
                    {dayjs(date).format("dddd Do")}
                  </h3>
                  <button
                    className="button-icon"
                    data-cy="dashboard-viewings-calendar-day-close"
                    onClick={() =>
                      pipe(O.none, updateCalendarDayModel, dispatch)
                    }
                  >
                    <Close />
                  </button>
                </div>
                <div className="card lg:max-h-[415px] overflow-y-auto shadow-md">
                  {pipe(
                    entries,
                    A.map(viewing => Viewing(viewing, dispatch))
                  )}
                </div>
              </>
            )
          )
        )}
      </div>
    </div>
  )
}

const moreInfoView = (model: M.Model): React_.Html<M.Msg> => {
  return dispatch => (
    <>
      <div className="sm:bg-stone sm:p-4 rounded-xl">
        <h3 className="heading mb-4">Need to adjust your calendar?</h3>
        <p className="mb-4">Click below to manage your availability.</p>
        <div className="card">
          <button
            className="button primary w-full text-md text-center"
            onClick={() => pipe(M.Msg.toggleCalendarSetup(), dispatch)}
            data-cy="dashboard-viewings-toggle-calendar"
          >
            Calendar Settings
          </button>
        </div>
      </div>
      {Modal({
        isOpen: model.isCalendarSetupOpen,
        closeMsg: M.Msg.toggleCalendarSetup(),
        containerStyles: "card bg-white p-6",
        body: innerDispatch => (
          <CalendarSetupView model={model} dispatch={innerDispatch} />
        ),
      })(dispatch)}
      {pipe(
        model.cancellationRequest,
        O.match(
          () => null,
          () =>
            Modal({
              isOpen: true,
              closeMsg: M.Msg.closeCancellationModal(),
              containerStyles: "card bg-white p-6",
              body: innerDispatch => (
                <CancellationModalView model={model} dispatch={innerDispatch} />
              ),
            })(dispatch)
        )
      )}
    </>
  )
}

const CalendarOverrideView = ({
  model,
  dispatch,
}: {
  model: M.Model
  dispatch: (msg: M.Msg) => void
}) => {
  // Generate all available time slots (30-minute intervals)
  const availableTimeSlots = pipe(
    Types.generateMultipleTimeSlots({
      calendarAvailability: {
        0: O.some([{ start: "01:00", end: "24:00" }]),
        1: O.some([{ start: "01:00", end: "24:00" }]),
        2: O.some([{ start: "01:00", end: "24:00" }]),
        3: O.some([{ start: "01:00", end: "24:00" }]),
        4: O.some([{ start: "01:00", end: "24:00" }]),
        5: O.some([{ start: "01:00", end: "24:00" }]),
        6: O.some([{ start: "01:00", end: "24:00" }]),
      },
      excludeLastTimeSlot: false,
    }),
    record => record[0],
    O.fromNullable,
    O.chain(O.fromNullable),
    O.map(A.flatten),
    O.getOrElse(() => [] as string[])
  )

  // Check if override should be displayed (end date >= today)
  const shouldDisplayOverride = (
    override: Types.CalendarOverrideDatesTimes
  ) => {
    const today = dayjs().startOf("day")
    const endDate = dayjs(override.dates[1]).startOf("day")
    return endDate.isAfter(today) || endDate.isSame(today)
  }

  return (
    <div className="flex flex-col gap-4">
      <h5 className="font-semibold">Calendar Overrides</h5>
      <p className="text-sm text-gray-600">
        Block specific dates or set custom availability for date ranges
      </p>
      <div className="flex flex-col gap-3">
        {pipe(
          model.calendarSetup.calendarOverride,
          O.match(
            () => (
              <button
                className="text-sm text-slate hover:text-slate-dark w-fit"
                onClick={() =>
                  pipe(M.Msg.onAddAvailabilityOverride(), dispatch)
                }
              >
                + Add Override
              </button>
            ),
            overrides => {
              const hasVisibleOverrides = overrides.some(shouldDisplayOverride)
              return !hasVisibleOverrides ? (
                <button
                  className="text-sm text-slate hover:text-slate-dark w-fit"
                  onClick={() =>
                    pipe(M.Msg.onAddAvailabilityOverride(), dispatch)
                  }
                >
                  + Add Override
                </button>
              ) : (
                <>
                  {overrides.map((override, idx) =>
                    !shouldDisplayOverride(override) ? null : (
                      <div
                        key={idx}
                        className="flex flex-col gap-2 p-3 bg-stone-light rounded-md"
                      >
                        <div className="flex items-start gap-2">
                          <div className="flex flex-col gap-2 flex-1">
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-medium w-16">
                                From:
                              </label>
                              <input
                                type="date"
                                className="rounded-md border border-gray-300 px-2 py-1 text-sm flex-1"
                                value={override.dates[0]}
                                onChange={e =>
                                  pipe(
                                    {
                                      blockedDates: O.some([
                                        e.target.value,
                                        override.dates[1],
                                      ] as [string, string]),
                                      idx,
                                    },
                                    M.Msg.onChangeAvailabilityOverrideDates,
                                    dispatch
                                  )
                                }
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-medium w-16">
                                To:
                              </label>
                              <input
                                type="date"
                                className="rounded-md border border-gray-300 px-2 py-1 text-sm flex-1"
                                value={override.dates[1]}
                                onChange={e =>
                                  pipe(
                                    {
                                      blockedDates: O.some([
                                        override.dates[0],
                                        e.target.value,
                                      ] as [string, string]),
                                      idx,
                                    },
                                    M.Msg.onChangeAvailabilityOverrideDates,
                                    dispatch
                                  )
                                }
                              />
                            </div>
                          </div>
                          <button
                            className="text-red-600 hover:text-red-800 text-sm mt-1"
                            onClick={() =>
                              pipe(
                                idx,
                                M.Msg.onRemoveAvailabilityOverride,
                                dispatch
                              )
                            }
                          >
                            Remove
                          </button>
                        </div>

                        <div className="flex flex-col gap-2 mt-2">
                          <span className="text-sm font-medium">
                            Available time slots (leave empty to block
                            entirely):
                          </span>
                          {override.times.map((timeSlot, innerIdx) => (
                            <div
                              key={innerIdx}
                              className="flex items-center gap-2"
                            >
                              <select
                                className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                                value={timeSlot.start}
                                onChange={e =>
                                  pipe(
                                    {
                                      timeSlot: O.some({
                                        start: e.target.value,
                                        end: timeSlot.end,
                                      }),
                                      outerIdx: idx,
                                      innerIdx,
                                    },
                                    M.Msg.onChangeAvailabilityOverrideTimeSlot,
                                    dispatch
                                  )
                                }
                              >
                                <option value="">Select start time</option>
                                {availableTimeSlots.map(time => (
                                  <option key={time} value={time}>
                                    {time}
                                  </option>
                                ))}
                              </select>
                              <span>to</span>
                              <select
                                className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                                value={timeSlot.end}
                                onChange={e =>
                                  pipe(
                                    {
                                      timeSlot: O.some({
                                        start: timeSlot.start,
                                        end: e.target.value,
                                      }),
                                      outerIdx: idx,
                                      innerIdx,
                                    },
                                    M.Msg.onChangeAvailabilityOverrideTimeSlot,
                                    dispatch
                                  )
                                }
                              >
                                <option value="">Select end time</option>
                                {availableTimeSlots.map(time => (
                                  <option key={time} value={time}>
                                    {time}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ))}
                          <button
                            className="text-sm text-slate hover:text-slate-dark w-fit"
                            onClick={() =>
                              pipe(
                                { idx, timeSlot: O.none },
                                M.Msg.onAddAvailabilityOverrideTimeSlot,
                                dispatch
                              )
                            }
                          >
                            + Add Time Slot
                          </button>
                        </div>
                      </div>
                    )
                  )}
                  <button
                    className="text-sm text-slate hover:text-slate-dark w-fit"
                    onClick={() =>
                      pipe(M.Msg.onAddAvailabilityOverride(), dispatch)
                    }
                  >
                    + Add Another Override
                  </button>
                </>
              )
            }
          )
        )}
      </div>
    </div>
  )
}

const NoticePeriodView = ({
  model,
  dispatch,
}: {
  model: M.Model
  dispatch: (msg: M.Msg) => void
}) => (
  <div className="flex flex-col gap-4">
    <h5 className="font-semibold">Minimum Notice Period</h5>
    <p className="text-sm text-gray-600">
      Set the minimum advance notice required for booking viewings
    </p>
    <div className="flex flex-row gap-4 items-center">
      <span className="text-sm text-gray-600">Minimum notice (hours)</span>
      <input
        type="number"
        className="max-w-[100px] rounded-md border border-gray-300 px-3 py-2"
        value={pipe(
          model.calendarSetup.calendarNoticePeriodHours,
          O.getOrElse(() => Types.defaultMinimumNoticePeriodHours)
        )}
        onChange={e =>
          pipe(
            Number(e.currentTarget.value),
            M.Msg.onChangeMinimumNotice,
            dispatch
          )
        }
      />
    </div>
  </div>
)

const PausedCalendarView = () => {
  return (
    <div className="flex flex-col gap-6 items-center text-center py-8">
      <div className="bg-stone-light rounded-full p-6 w-20 h-20 flex items-center justify-center">
        <svg
          className="w-10 h-10 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <div>
        <h2 className="text-2xl font-semibold mb-3">Calendar Paused</h2>
        <p className="text-gray-600 max-w-md mx-auto">
          Your property's calendar is currently paused. Please contact{" "}
          <a
            href="mailto:view@flyp.co"
            className="text-brick hover:text-brick-dark underline"
          >
            view@flyp.co
          </a>{" "}
          for further information.
        </p>
      </div>
    </div>
  )
}

const CalendarSetupView = ({
  model,
  dispatch,
}: {
  model: M.Model
  dispatch: (msg: M.Msg) => void
}) => {
  // Safety check - ensure calendarSetup exists
  if (!model.calendarSetup) {
    return (
      <p className="text-red-600">Error: Calendar setup data not available</p>
    )
  }

  // Show paused view if calendar is paused
  if (model.calendarSetup.calendarPaused) {
    return <PausedCalendarView />
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Calendar Settings</h2>
        <p className="text-gray-600">
          Set your weekly availability for property viewings
        </p>
      </div>

      <NoticePeriodView model={model} dispatch={dispatch} />

      <CalendarAvailabilityView model={model} dispatch={dispatch} />

      <CalendarOverrideView model={model} dispatch={dispatch} />

      <div className="flex flex-col gap-4 pt-4 border-t border-gray-200">
        {pipe(
          model.calendarSetup.submitAvailability,
          E.match(
            status =>
              status._tag === "error" ? (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-500"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-medium text-red-800">
                        {status.value}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null,
            () => null
          )
        )}
        <button
          className="button primary w-full"
          onClick={() => pipe(M.Msg.updateCalendarSettingsReq(), dispatch)}
          data-cy="dashboard-viewings-update-calendar"
          disabled={pipe(
            model.calendarSetup.submitAvailability,
            E.match(
              status => status._tag === "loading",
              () => false
            )
          )}
        >
          {pipe(
            model.calendarSetup.submitAvailability,
            E.match(
              status =>
                status._tag === "loading" ? "Updating..." : "Save Changes",
              () => "Save Changes"
            )
          )}
        </button>
      </div>
    </div>
  )
}

const CalendarAvailabilityView = ({
  model,
  dispatch,
}: {
  model: M.Model
  dispatch: (msg: M.Msg) => void
}) => {
  const daysOfWeek: Array<{
    key: keyof Types.CalendarAvailability
    name: string
  }> = [
    { key: "1", name: "monday" },
    { key: "2", name: "tuesday" },
    { key: "3", name: "wednesday" },
    { key: "4", name: "thursday" },
    { key: "5", name: "friday" },
    { key: "6", name: "saturday" },
  ]

  // Get the availability from newCalendarAvailability (which now always has a default)
  const availability = pipe(
    O.fromNullable(model.calendarSetup?.newCalendarAvailability),
    O.flatten,
    O.getOrElse(() => Types.defaultCalendarAvailability)
  )

  return (
    <div className="flex flex-col gap-4">
      <h5 className="font-semibold">Weekly Availability</h5>
      <div className="flex flex-col gap-3">
        {daysOfWeek.map(day => {
          // Get day slots safely - might be an Option or null/undefined
          const daySlots = pipe(
            O.fromNullable(availability?.[day.key]),
            O.flatten
          )
          return (
            <DayAvailabilityView
              key={day.key}
              dayKey={day.key}
              dayName={day.name}
              slots={daySlots}
              dispatch={dispatch}
            />
          )
        })}
      </div>
    </div>
  )
}

const DayAvailabilityView = ({
  dayKey,
  dayName,
  slots,
  dispatch,
}: {
  dayKey: keyof Types.CalendarAvailability
  dayName: string
  slots: O.Option<Types.CalendarStartEndTimes[]>
  dispatch: (msg: M.Msg) => void
}) => {
  // Generate all available time slots (30-minute intervals)
  const availableTimeSlots = pipe(
    Types.generateMultipleTimeSlots({
      calendarAvailability: {
        0: O.some([{ start: "01:00", end: "24:00" }]),
        1: O.some([{ start: "01:00", end: "24:00" }]),
        2: O.some([{ start: "01:00", end: "24:00" }]),
        3: O.some([{ start: "01:00", end: "24:00" }]),
        4: O.some([{ start: "01:00", end: "24:00" }]),
        5: O.some([{ start: "01:00", end: "24:00" }]),
        6: O.some([{ start: "01:00", end: "24:00" }]),
      },
      excludeLastTimeSlot: false,
    }),
    record => record[dayKey],
    O.fromNullable,
    O.chain(O.fromNullable),
    O.map(A.flatten),
    O.getOrElse(() => [] as string[])
  )

  const hasAvailability = pipe(
    slots,
    O.exists(slotsArray => A.isNonEmpty(slotsArray))
  )

  return (
    <div
      className={`flex flex-col gap-2 p-3 rounded-md ${
        hasAvailability ? "bg-stone-light" : "bg-gray-50"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="font-medium capitalize">{dayName}</span>
        <button
          className="text-sm text-slate hover:text-slate-dark"
          onClick={() => pipe(dayKey, M.Msg.addAvailabilityTimeSlot, dispatch)}
        >
          + Add Time Slot
        </button>
      </div>
      {pipe(
        slots,
        O.match(
          () => <p className="text-sm text-gray-500">No availability</p>,
          slotsArray =>
            A.isEmpty(slotsArray) ? (
              <p className="text-sm text-gray-500">No availability</p>
            ) : (
              <div className="flex flex-col gap-2">
                {slotsArray.map((slot, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <select
                      className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                      value={slot.start}
                      onChange={e =>
                        pipe(
                          {
                            setField: {
                              [dayKey]: O.some({
                                start: e.target.value,
                                end: slot.end,
                              }),
                            },
                            idx,
                          },
                          M.Msg.onChangeCalendarAvailability,
                          dispatch
                        )
                      }
                    >
                      {availableTimeSlots.map(time => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                    <span>to</span>
                    <select
                      className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                      value={slot.end}
                      onChange={e =>
                        pipe(
                          {
                            setField: {
                              [dayKey]: O.some({
                                start: slot.start,
                                end: e.target.value,
                              }),
                            },
                            idx,
                          },
                          M.Msg.onChangeCalendarAvailability,
                          dispatch
                        )
                      }
                    >
                      {availableTimeSlots.map(time => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                    <button
                      className="text-red-600 hover:text-red-800 text-sm"
                      onClick={() =>
                        pipe(dayKey, M.Msg.removeAvailabilityTimeSlot, dispatch)
                      }
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )
        )
      )}
    </div>
  )
}

const CancellationModalView = ({
  model,
  dispatch,
}: {
  model: M.Model
  dispatch: (msg: M.Msg) => void
}) => {
  return pipe(
    model.cancellationRequest,
    O.match(
      () => <div>Loading...</div>,
      request => (
        <div className="flex flex-col gap-4">
          <h2 className="text-xl font-semibold">
            Request viewing cancellation
          </h2>

          <div className="bg-stone-light p-4 rounded-md">
            <p className="text-sm text-gray-600 mb-1">Viewing details</p>
            <p className="font-medium">
              {request.viewing.viewingDate.toLocaleString("en-GB", {
                dateStyle: "full",
                timeStyle: "short",
                timeZone: "Europe/London",
              })}
            </p>
            <p className="text-gray-700 mt-1">
              Agent: {request.viewing.agent.brand}
            </p>
          </div>

          <div>
            <label
              htmlFor="cancellation-reason"
              className="block text-sm font-medium mb-2"
            >
              Reason for cancellation <span className="text-brick">*</span>
            </label>
            <textarea
              id="cancellation-reason"
              className="w-full rounded-md border border-gray-300 px-3 py-2 min-h-[120px]"
              placeholder="Please provide a reason for cancelling this viewing..."
              value={request.reason}
              onChange={e =>
                pipe(e.target.value, M.Msg.changeCancellationReason, dispatch)
              }
              data-cy="cancellation-reason-textarea"
            />
          </div>

          {pipe(
            request.submitStatus,
            E.match(
              status =>
                status._tag === "error" ? (
                  <div
                    className="text-brick text-sm"
                    data-cy="cancellation-error"
                  >
                    {status.value}
                  </div>
                ) : null,
              () => (
                <div
                  className="text-lawn text-sm"
                  data-cy="cancellation-success"
                >
                  Cancellation request submitted successfully
                </div>
              )
            )
          )}

          <div className="flex gap-3 justify-end">
            <button
              className="button secondary"
              onClick={() => pipe(M.Msg.closeCancellationModal(), dispatch)}
              data-cy="cancellation-cancel-btn"
            >
              Cancel
            </button>
            <button
              className="button primary"
              onClick={() => pipe(M.Msg.submitCancellationReq(), dispatch)}
              disabled={
                request.reason.trim().length === 0 ||
                pipe(
                  request.submitStatus,
                  E.match(
                    status => status._tag === "loading",
                    () => false
                  )
                )
              }
              data-cy="cancellation-submit-btn"
            >
              {pipe(
                request.submitStatus,
                E.match(
                  status =>
                    status._tag === "loading"
                      ? "Submitting..."
                      : "Submit request",
                  () => "Submitted"
                )
              )}
            </button>
          </div>
        </div>
      )
    )
  )
}
